import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Button from "@/components/site/Button";
import RichTextEditor from "@/components/admin/RichTextEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAANDEN = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

function fmtDatum(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.getDate()} ${MAANDEN[d.getMonth()]} ${d.getFullYear()}, ${d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Amsterdam" })}`;
}

const isEmptyVal = (v) => v === "" || v === null || v === undefined || (Array.isArray(v) && v.length === 0);

// ---------------------------------------------------------------------------
// Lijst van bewerkbare pagina's
// ---------------------------------------------------------------------------
export default function PagesEditor({ onUnauthorized }) {
    const [pages, setPages] = useState(null);
    const [active, setActive] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        axios
            .get(`${API}/admin/content/pages/list`, { withCredentials: true })
            .then((r) => setPages(r.data.pages))
            .catch((err) => {
                if (err.response?.status === 401) return onUnauthorized();
                setError("De paginalijst kon niet geladen worden.");
            });
    }, [onUnauthorized]);

    if (active) {
        return <PageEditor page={active} onBack={() => setActive(null)} onUnauthorized={onUnauthorized} />;
    }

    return (
        <div data-testid="admin-pages">
            <h1 className="font-[Fraunces] text-[32px] md:text-[38px] leading-tight mb-2" style={{ fontWeight: 300 }}>
                Pagina's
            </h1>
            <p className="text-[14.5px] text-[var(--hds-ink)]/65 mb-8 max-w-[60ch]">
                Kies een pagina om de teksten, foto's en SEO-gegevens aan te passen.
            </p>
            {error && <p className="text-[14px] text-[var(--hds-clay-dark)] mb-4" data-testid="pages-error">{error}</p>}
            {!pages ? (
                <p className="text-[var(--hds-ink)]/50">Laden...</p>
            ) : (
                <ul className="divide-y divide-[var(--hds-line)] border-y border-[var(--hds-line)]" data-testid="admin-pages-list">
                    {pages.map((p) => (
                        <li key={p.page}>
                            <button
                                type="button"
                                onClick={() => setActive(p)}
                                data-testid={`admin-page-${p.page}`}
                                className="w-full flex items-center justify-between gap-4 py-4 px-2 text-left group"
                            >
                                <span>
                                    <span className="block text-[15.5px] text-[var(--hds-ink)] group-hover:text-[var(--hds-clay)] transition-colors duration-300">
                                        {p.label}
                                    </span>
                                    <span className="block text-[12.5px] text-[var(--hds-ink)]/45 mt-0.5">{p.path}</span>
                                </span>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden className="text-[var(--hds-clay)] shrink-0 transition-transform duration-300 group-hover:translate-x-1">
                                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Editor voor één pagina
// ---------------------------------------------------------------------------
function PageEditor({ page, onBack, onUnauthorized }) {
    const [blocks, setBlocks] = useState(null); // originele waarden van de server
    const [values, setValues] = useState({}); // key -> { value, alt }
    const [historyFor, setHistoryFor] = useState(null);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState("");
    const [error, setError] = useState("");
    const toastTimer = useRef(null);

    useEffect(() => {
        axios
            .get(`${API}/admin/content`, { params: { page: page.page }, withCredentials: true })
            .then((r) => {
                setBlocks(r.data.blocks);
                const v = {};
                r.data.blocks.forEach((b) => {
                    v[b.key] = { value: b.value, alt: b.alt };
                });
                setValues(v);
            })
            .catch((err) => {
                if (err.response?.status === 401) return onUnauthorized();
                setError("De inhoud kon niet geladen worden.");
            });
    }, [page.page, onUnauthorized]);

    const showToast = (msg) => {
        setToast(msg);
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(""), 4000);
    };

    const setVal = (key, patch) =>
        setValues((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

    const isDirty = (b) => {
        const v = values[b.key];
        if (!v) return false;
        return JSON.stringify(v.value) !== JSON.stringify(b.value) || (v.alt ?? null) !== (b.alt ?? null);
    };

    const save = async () => {
        setError("");
        const dirty = blocks.filter(isDirty);
        if (!dirty.length) return;
        // alt-tekst is verplicht bij foto's
        for (const b of dirty) {
            if (b.type === "image" && !String(values[b.key].alt || "").trim()) {
                setError(`Alt-tekst is verplicht bij "${b.label}".`);
                return;
            }
        }
        setSaving(true);
        let savedAny = false;
        let skipped = 0;
        try {
            for (const b of dirty) {
                const v = values[b.key];
                let value = v.value;
                if (Array.isArray(value)) value = value.filter((x) => String(x).trim() !== "");
                let allowEmpty = false;
                if (isEmptyVal(value) && !isEmptyVal(b.value)) {
                    if (!window.confirm(`Dit veld leegmaken?\n\n${b.label}`)) {
                        skipped += 1;
                        continue;
                    }
                    allowEmpty = true;
                }
                const r = await axios.put(
                    `${API}/admin/content/${encodeURIComponent(b.key)}`,
                    { value, alt: v.alt, allow_empty: allowEmpty },
                    { withCredentials: true }
                );
                if (r.data?.changed) savedAny = true;
            }
            // ververs de originele waarden
            const r = await axios.get(`${API}/admin/content`, { params: { page: page.page }, withCredentials: true });
            setBlocks(r.data.blocks);
            const nv = {};
            r.data.blocks.forEach((b) => {
                nv[b.key] = { value: b.value, alt: b.alt };
            });
            setValues(nv);
            if (savedAny) {
                showToast("Opgeslagen. De pagina is bijgewerkt.");
            } else if (skipped > 0) {
                showToast("Geen wijzigingen opgeslagen.");
            }
        } catch (err) {
            if (err.response?.status === 401) return onUnauthorized();
            setError(err.response?.data?.detail || "Opslaan is mislukt. Probeer het opnieuw.");
        } finally {
            setSaving(false);
        }
    };

    const onRestored = (key, block) => {
        setVal(key, { value: block.value, alt: block.alt });
        setBlocks((prev) => prev.map((b) => (b.key === key ? { ...b, ...block } : b)));
        setHistoryFor(null);
        showToast("Versie teruggezet.");
    };

    if (!blocks) {
        return <p className="text-[var(--hds-ink)]/50">{error || "Laden..."}</p>;
    }

    const seoBlocks = blocks.filter((b) => b.key.endsWith(".seo-title") || b.key.endsWith(".seo-description"));
    const contentBlocks = blocks.filter((b) => !seoBlocks.includes(b));
    const dirtyCount = blocks.filter(isDirty).length;

    return (
        <div data-testid="page-editor">
            <button
                type="button"
                onClick={onBack}
                data-testid="page-editor-back"
                className="text-[13px] text-[var(--hds-ink)]/60 hover:text-[var(--hds-clay)] underline underline-offset-2 mb-6"
            >
                Terug naar pagina's
            </button>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
                <div>
                    <h1 className="font-[Fraunces] text-[30px] md:text-[36px] leading-tight" style={{ fontWeight: 300 }}>
                        {page.label}
                    </h1>
                    <a
                        href={page.path}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="page-editor-view-link"
                        className="inline-block mt-1 text-[13px] text-[var(--hds-clay)] underline underline-offset-2"
                    >
                        Bekijk pagina
                    </a>
                </div>
                <Button
                    variant="primary"
                    onClick={save}
                    disabled={saving || dirtyCount === 0}
                    data-testid="page-save"
                >
                    {saving ? "Opslaan..." : dirtyCount ? `Opslaan (${dirtyCount})` : "Opslaan"}
                </Button>
            </div>

            {error && (
                <p className="mb-6 text-[14px] text-[var(--hds-clay-dark)]" data-testid="page-editor-error">{error}</p>
            )}

            {/* SEO */}
            <section className="mb-10 rounded-[14px] border border-[var(--hds-line)] bg-[var(--hds-card)]/60 p-6 md:p-7" data-testid="page-editor-seo">
                <h2 className="text-[11.5px] tracking-[0.24em] uppercase text-[var(--hds-clay)] mb-5">SEO</h2>
                <div className="space-y-5">
                    {seoBlocks.map((b) => (
                        <SeoEditor
                            key={b.key}
                            block={b}
                            value={values[b.key]?.value ?? ""}
                            onChange={(val) => setVal(b.key, { value: val })}
                        />
                    ))}
                </div>
            </section>

            {/* Inhoudsblokken */}
            <div className="space-y-6">
                {contentBlocks.map((b) => (
                    <div
                        key={b.key}
                        data-testid={`block-${b.key}`}
                        className="rounded-[14px] border border-[var(--hds-line)] bg-[var(--hds-card)] p-5 md:p-6"
                    >
                        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                            <div>
                                <p className="text-[14px] font-semibold text-[var(--hds-ink)]">{b.label}</p>
                                {b.admin_modified && (
                                    <p className="text-[11.5px] text-[var(--hds-ink)]/45 mt-0.5" data-testid={`block-modified-${b.key}`}>
                                        Aangepast{ b.updated_by ? ` door ${b.updated_by}` : ""}{ b.updated_at ? ` op ${fmtDatum(b.updated_at)}` : ""}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setHistoryFor(historyFor === b.key ? null : b.key)}
                                data-testid={`block-history-${b.key}`}
                                className="text-[12.5px] text-[var(--hds-ink)]/55 hover:text-[var(--hds-clay)] underline underline-offset-2"
                            >
                                Geschiedenis
                            </button>
                        </div>
                        <BlockEditor
                            block={b}
                            value={values[b.key] || { value: b.value, alt: b.alt }}
                            onChange={(patch) => setVal(b.key, patch)}
                        />
                        {historyFor === b.key && (
                            <HistoryPanel blockKey={b.key} onRestored={onRestored} onUnauthorized={onUnauthorized} />
                        )}
                    </div>
                ))}
            </div>

            <div className="mt-8 flex justify-end">
                <Button variant="primary" onClick={save} disabled={saving || dirtyCount === 0} data-testid="page-save-bottom">
                    {saving ? "Opslaan..." : dirtyCount ? `Opslaan (${dirtyCount})` : "Opslaan"}
                </Button>
            </div>

            {toast && (
                <div
                    role="status"
                    data-testid="page-saved-toast"
                    className="fixed bottom-6 right-6 z-50 rounded-[12px] bg-[var(--hds-green)] text-[var(--hds-card)] px-5 py-3.5 text-[14.5px] shadow-[0_16px_48px_rgba(44,39,34,0.25)]"
                >
                    {toast}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Blok-editors per type
// ---------------------------------------------------------------------------
function BlockEditor({ block, value, onChange }) {
    if (block.type === "list") return <ListEditor block={block} value={value} onChange={onChange} />;
    if (block.type === "image") return <ImageEditor block={block} value={value} onChange={onChange} />;
    if (block.type === "richtext")
        return (
            <RichTextEditor
                value={value.value}
                onChange={(v) => onChange({ value: v })}
                testid={`block-richtext-${block.key}`}
            />
        );
    return <TextEditor block={block} value={value} onChange={onChange} />;
}

function TextEditor({ block, value, onChange }) {
    const text = value.value ?? "";
    const rows = Math.min(10, Math.max(2, Math.ceil(String(text).length / 70)));
    return (
        <textarea
            rows={rows}
            value={text}
            onChange={(e) => onChange({ value: e.target.value })}
            data-testid={`block-input-${block.key}`}
            className="w-full rounded-[10px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-3 text-[14.5px] leading-[1.6] transition-colors duration-300"
        />
    );
}

function SeoEditor({ block, value, onChange }) {
    const max = block.key.endsWith(".seo-title") ? 60 : 155;
    const len = String(value || "").length;
    return (
        <div data-testid={`seo-${block.key}`}>
            <div className="flex items-baseline justify-between gap-3 mb-2">
                <label className="text-[13px] text-[var(--hds-ink)]/70">{block.label}</label>
                <span
                    className={`text-[12px] ${len > max ? "text-[var(--hds-clay-dark)] font-semibold" : "text-[var(--hds-ink)]/45"}`}
                    data-testid={`seo-count-${block.key}`}
                >
                    {len}/{max}
                </span>
            </div>
            {block.key.endsWith(".seo-title") ? (
                <input
                    type="text"
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`block-input-${block.key}`}
                    className="w-full rounded-[10px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-2.5 text-[14.5px] transition-colors duration-300"
                />
            ) : (
                <textarea
                    rows={2}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`block-input-${block.key}`}
                    className="w-full rounded-[10px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-2.5 text-[14.5px] transition-colors duration-300"
                />
            )}
        </div>
    );
}

function ListEditor({ block, value, onChange }) {
    const items = Array.isArray(value.value) ? value.value : [];
    const dragIdx = useRef(null);

    const move = (from, to) => {
        if (from === null || from === to) return;
        const next = [...items];
        const [x] = next.splice(from, 1);
        next.splice(to, 0, x);
        onChange({ value: next });
    };

    return (
        <div data-testid={`block-list-${block.key}`}>
            <ul className="space-y-2">
                {items.map((item, i) => (
                    <li
                        key={i}
                        draggable
                        onDragStart={() => (dragIdx.current = i)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => move(dragIdx.current, i)}
                        className="flex items-center gap-2 rounded-[10px] bg-[var(--hds-sand)] border border-[var(--hds-line)] px-3 py-2"
                        data-testid={`block-list-item-${block.key}-${i}`}
                    >
                        <span aria-hidden className="cursor-grab text-[var(--hds-ink)]/35 shrink-0" title="Versleep om te ordenen">
                            <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
                                <circle cx="2.5" cy="3" r="1.3" /><circle cx="7.5" cy="3" r="1.3" />
                                <circle cx="2.5" cy="8" r="1.3" /><circle cx="7.5" cy="8" r="1.3" />
                                <circle cx="2.5" cy="13" r="1.3" /><circle cx="7.5" cy="13" r="1.3" />
                            </svg>
                        </span>
                        <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                                const next = [...items];
                                next[i] = e.target.value;
                                onChange({ value: next });
                            }}
                            className="flex-1 bg-transparent outline-none text-[14px]"
                        />
                        <button
                            type="button"
                            onClick={() => onChange({ value: items.filter((_, k) => k !== i) })}
                            data-testid={`block-list-remove-${block.key}-${i}`}
                            className="shrink-0 text-[var(--hds-ink)]/40 hover:text-[var(--hds-clay)] text-[16px] leading-none px-1"
                            aria-label="Verwijder punt"
                        >
                            ×
                        </button>
                    </li>
                ))}
            </ul>
            <button
                type="button"
                onClick={() => onChange({ value: [...items, ""] })}
                data-testid={`block-list-add-${block.key}`}
                className="mt-3 text-[13px] text-[var(--hds-clay)] underline underline-offset-2"
            >
                + Punt toevoegen
            </button>
        </div>
    );
}

function ImageEditor({ block, value, onChange }) {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const fileRef = useRef(null);

    const upload = async (file) => {
        setError("");
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            setError("Bestand is te groot (max 8 MB).");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await axios.post(`${API}/admin/media`, fd, { withCredentials: true });
            onChange({ value: r.data.url });
        } catch (err) {
            setError(err.response?.data?.detail || "Uploaden is mislukt.");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div data-testid={`block-image-${block.key}`}>
            <div className="flex flex-col sm:flex-row gap-5">
                <div className="shrink-0">
                    {value.value ? (
                        <img
                            src={value.value}
                            alt={value.alt || ""}
                            className="w-full max-w-[240px] rounded-[10px] border border-[var(--hds-line)] object-cover"
                            data-testid={`block-image-preview-${block.key}`}
                        />
                    ) : (
                        <div className="w-[240px] h-[140px] rounded-[10px] border border-dashed border-[var(--hds-line)] flex items-center justify-center text-[12.5px] text-[var(--hds-ink)]/40">
                            Nog geen foto
                        </div>
                    )}
                </div>
                <div className="flex-1 space-y-3">
                    <div>
                        <label className="block text-[12px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-1.5">
                            Alt-tekst (verplicht)
                        </label>
                        <input
                            type="text"
                            value={value.alt ?? ""}
                            onChange={(e) => onChange({ alt: e.target.value })}
                            data-testid={`block-alt-${block.key}`}
                            className="w-full rounded-[10px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-2.5 text-[14px] transition-colors duration-300"
                        />
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => upload(e.target.files?.[0])}
                        className="hidden"
                        data-testid={`block-upload-input-${block.key}`}
                    />
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        data-testid={`block-upload-${block.key}`}
                        className="inline-flex items-center gap-2 rounded-full border border-[var(--hds-line)] bg-[var(--hds-sand)] px-4 py-2 text-[13.5px] text-[var(--hds-ink)] hover:border-[var(--hds-clay)] transition-colors duration-300 disabled:opacity-50"
                    >
                        {uploading ? "Uploaden..." : "Nieuwe foto uploaden"}
                    </button>
                    <p className="text-[12px] text-[var(--hds-ink)]/45">jpg, png of webp, max 8 MB. Wordt verkleind en als webp opgeslagen.</p>
                    {error && <p className="text-[13px] text-[var(--hds-clay-dark)]" data-testid={`block-upload-error-${block.key}`}>{error}</p>}
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Geschiedenis
// ---------------------------------------------------------------------------
function HistoryPanel({ blockKey, onRestored, onUnauthorized }) {
    const [entries, setEntries] = useState(null);
    const [error, setError] = useState("");
    const [restoring, setRestoring] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/admin/content/${encodeURIComponent(blockKey)}/history`, { withCredentials: true })
            .then((r) => setEntries(r.data.history))
            .catch((err) => {
                if (err.response?.status === 401) return onUnauthorized();
                setError("Geschiedenis kon niet geladen worden.");
            });
    }, [blockKey, onUnauthorized]);

    const restore = async (entry) => {
        if (!window.confirm("Deze versie terugzetten?")) return;
        setRestoring(entry.id);
        try {
            const r = await axios.post(
                `${API}/admin/content/${encodeURIComponent(blockKey)}/restore`,
                { history_id: entry.id },
                { withCredentials: true }
            );
            onRestored(blockKey, r.data.block);
        } catch (err) {
            if (err.response?.status === 401) return onUnauthorized();
            setError("Terugzetten is mislukt.");
        } finally {
            setRestoring(null);
        }
    };

    const preview = (v) => {
        if (Array.isArray(v)) return v.join(" · ");
        const s = String(v ?? "");
        return s.length > 160 ? s.slice(0, 160) + "..." : s;
    };

    return (
        <div className="mt-4 pt-4 border-t border-[var(--hds-line)]" data-testid={`history-panel-${blockKey}`}>
            {error && <p className="text-[13px] text-[var(--hds-clay-dark)]">{error}</p>}
            {!entries ? (
                <p className="text-[13px] text-[var(--hds-ink)]/50">Laden...</p>
            ) : entries.length === 0 ? (
                <p className="text-[13px] text-[var(--hds-ink)]/50" data-testid={`history-empty-${blockKey}`}>
                    Nog geen eerdere versies.
                </p>
            ) : (
                <ul className="space-y-3">
                    {entries.map((e) => (
                        <li
                            key={e.id}
                            className="flex items-start justify-between gap-4 rounded-[10px] bg-[var(--hds-sand)] px-4 py-3"
                            data-testid={`history-entry-${e.id}`}
                        >
                            <div className="min-w-0">
                                <p className="text-[12px] text-[var(--hds-ink)]/50 mb-1">
                                    {fmtDatum(e.changed_at)}{e.changed_by ? ` · ${e.changed_by}` : ""}
                                </p>
                                <p className="text-[13.5px] leading-[1.5] text-[var(--hds-ink)]/80 break-words">
                                    {preview(e.prev_value)}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => restore(e)}
                                disabled={restoring === e.id}
                                data-testid={`history-restore-${e.id}`}
                                className="shrink-0 text-[12.5px] text-[var(--hds-clay)] underline underline-offset-2 disabled:opacity-50"
                            >
                                {restoring === e.id ? "Bezig..." : "Terugzetten"}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
